"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultAnswersVisitor = void 0;
exports.defaultQuestionnaireAnswers = defaultQuestionnaireAnswers;
const uuid_1 = require("uuid");
const evaluateConditions_1 = require("../condition-engine/evaluateConditions");
const operators_1 = require("../types/carrierQuestionnaire/conditions/operators");
const ExpandedRepetitionsVisitor_1 = require("./expandedContext/ExpandedRepetitionsVisitor");
const defaultIf_1 = require("./nodeEvaluation/defaultIf/defaultIf");
const filterVisibleAnswers_1 = require("./nodeEvaluation/visibleIf/filterVisibleAnswers");
const questionnaire_1 = require("./questionnaire");
const structure_1 = require("./structure");
class DefaultAnswersVisitor extends ExpandedRepetitionsVisitor_1.ExpandedRepetitionsVisitor {
    visibilityDependencyMap;
    changedNodeInstances = [];
    timezone;
    answersResolver; // This is shadowing the base class property because it requires more permission here.
    currentDateOverride;
    constructor(answersResolver, visibilityDependencyMap, timezone, currentDateOverride) {
        super(answersResolver);
        this.answersResolver = answersResolver;
        this.visibilityDependencyMap = visibilityDependencyMap;
        this.timezone = timezone;
        this.currentDateOverride = currentDateOverride;
    }
    getChangedNodeInstances() {
        return [...this.changedNodeInstances];
    }
    visitRepeatedSectionGroup(sectionGroup) {
        if (!sectionGroup.readOnly) {
            this.setDefaultSurrogateId(sectionGroup.blueprintId);
            super.visitRepeatedSectionGroup(sectionGroup);
        }
    }
    visitRepeatedFieldGroup(fieldGroup, repetitionIndex) {
        if (!fieldGroup.readOnly) {
            if ((0, structure_1.isRepeatableOptionsBasedOnCollection)(fieldGroup.options)) {
                this.setDefaultSurrogateId(fieldGroup.blueprintId, fieldGroup.options.expandToCollectionLength, repetitionIndex);
            }
            else {
                this.setDefaultSurrogateId(fieldGroup.blueprintId);
            }
            super.visitRepeatedFieldGroup(fieldGroup, repetitionIndex);
        }
    }
    visitField(field) {
        this.setDefaultFieldValue(field);
    }
    setDefaultSurrogateId(blueprintId, collectionBlueprintId, repetitionIndex) {
        const scope = this.repeatedInstanceIdentifiers();
        if (typeof scope.byBlueprintId[blueprintId] === 'undefined') {
            // If no index is provided we cannot create a surrogateId for a collection item.
            return;
        }
        const result = this.answersResolver.getInstanceId(blueprintId, scope.byBlueprintId);
        if (!result.success) {
            let surrogateId = undefined;
            if (scope && collectionBlueprintId && repetitionIndex) {
                //If we have an index and a collection ID, use the surrogateId from the same index in the collection.
                const correspondingItemInAnotherCollectionItem = this.answersResolver.getAnswer(collectionBlueprintId, {
                    [collectionBlueprintId]: repetitionIndex,
                });
                surrogateId = correspondingItemInAnotherCollectionItem?.surrogateId || (0, uuid_1.v4)();
            }
            else {
                surrogateId = (0, uuid_1.v4)();
            }
            this.answersResolver.setInstanceId(blueprintId, scope.byBlueprintId, surrogateId);
        }
    }
    setDefaultFieldValue(field) {
        const { defaultValue: defaultFieldValue, nodeId, defaultIf, blueprintId } = field;
        if (!nodeId) {
            return;
        }
        if (typeof defaultFieldValue === 'undefined' && typeof defaultIf === 'undefined') {
            return;
        }
        const scopes = this.repeatedInstanceIdentifiers();
        if (!this.isFieldVisible(field, scopes.byNodeId)) {
            // Only set defaults if the field is visible.
            return;
        }
        const defaultValue = field.defaultIf
            ? (0, defaultIf_1.getConditionalDefaultValueForField)(field.defaultIf, this.answersResolver, scopes.byNodeId, this.timezone, this.currentDateOverride)
            : defaultFieldValue;
        const currentAnswer = this.answersResolver.usingNodeId().getAnswer(nodeId, scopes.byNodeId);
        const initialFieldValue = (0, questionnaire_1.getInitialFieldValue)(field);
        if (currentAnswer === defaultValue ||
            (typeof currentAnswer !== 'undefined' && currentAnswer !== initialFieldValue)) {
            return;
        }
        this.answersResolver.usingNodeId().setAnswer(defaultValue, nodeId, scopes.byNodeId);
        this.changedNodeInstances.push({
            byBlueprintId: {
                blueprintId: blueprintId,
                blueprintIdScope: scopes.byBlueprintId,
            },
            byNodeId: {
                nodeId: field.nodeId,
                nodeIdScope: scopes.byNodeId,
            },
        });
    }
    isFieldVisible(field, scope) {
        let fieldVisibilityConditions = undefined;
        if (field.nodeId) {
            fieldVisibilityConditions = this.visibilityDependencyMap.getVisibilityConditions(field.nodeId);
        }
        const visibilityConditions = fieldVisibilityConditions?.field;
        const isFieldVisible = !visibilityConditions ||
            (0, evaluateConditions_1.evaluateConditions)({ conditions: visibilityConditions, operator: operators_1.BooleanOperators.or }, this.answersResolver, scope, this.timezone, this.currentDateOverride).isValid;
        return isFieldVisible;
    }
}
exports.DefaultAnswersVisitor = DefaultAnswersVisitor;
function defaultQuestionnaireAnswers(questionnaire, answersResolver, dependencyMap, timezone, currentDateOverride) {
    const defaultAnswersVisitor = new DefaultAnswersVisitor(answersResolver, dependencyMap, timezone, currentDateOverride);
    defaultAnswersVisitor.visitQuestionnaire(questionnaire);
    // Setting defaults may affect the visibility of other answers.
    for (const nodeInstance of defaultAnswersVisitor.getChangedNodeInstances()) {
        if (nodeInstance.byNodeId.nodeId) {
            (0, filterVisibleAnswers_1.filterVisibleAnswers)(nodeInstance.byNodeId.nodeId, dependencyMap, answersResolver, nodeInstance.byNodeId.nodeIdScope, timezone, currentDateOverride);
        }
    }
}
